import React from 'react'
import { base } from 'lib/styleguide/base.scss'
import { connect } from 'react-redux'
import styles from './LiveLogPage.scss'
import { liveLogSelectors } from 'data/resources/liveLogs'
import { userActions, userSelectors } from 'data/resources/users'
import { tripSelectors } from 'data/resources/trips'
import { routeSelectors } from 'data/resources/routes'
import * as commentsActions from 'lib/api/comments'
import { CONTENT_COLLAPSE_WIDTH } from 'lib/layoutConstants'
import { pointOfInterestSelectors } from 'data/resources/pointsOfInterest'
import { photoSelectors } from 'data/resources/photos'
import { LiveLog } from 'data/records'
import { GlobalNav } from 'common/GlobalNav'
import { LiveLogStatusBar } from '../LiveLogStatusBar'
import { MetricUnitsContext } from 'common/units/MetricUnitsContext'
import { Loadable } from 'common/Loadable'
import { SidePanel } from './SidePanel'
import { MapPanel } from './MapPanel'
import { FullscreenButton, FullscreenContext } from 'common/FullscreenButton'
import moment from 'moment'
import cx from 'classnames'
import _ from 'lodash'
import { injectIntl } from 'react-intl'

const LL_UNSTARTED = 'LL_UNSTARTED' // we have a finalized liveLog record, and last update was > 24 hrs ago
const LL_COMPLETED = 'LL_COMPLETED' // we have a finalized liveLog record, and last update was <= 24 hrs ago
const LL_IN_PROGRESS = 'LL_IN_PROGRESS' // we have an unfinalized liveLog record

export class _LiveLogPage extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      collapse: window.innerWidth < CONTENT_COLLAPSE_WIDTH,
      isFullscreen: false,
      allowStatusBar: true,
      sbHeight: 0
    }
    this._throttledResize = _.throttle(this.onResize, 300, { trailing: true })
  }

  componentDidMount () {
    this._updateId = setInterval(this.update, 30000) // 30 seconds

    if (window.matchMedia) {
      // fancy callback way of tracking collapse state that doesn't need debounce
      this._mediaquery = window.matchMedia(`screen and (max-width: ${CONTENT_COLLAPSE_WIDTH}px)`)
      console.log('matchMedia', `screen and (max-width: ${CONTENT_COLLAPSE_WIDTH}px)`)
      this._mediaquery.addListener(this.onResize)
    } else {
      // dumb fallback
      window.addEventListener('resize', this._throttledResize)
    }
  }

  componentWillUnmount () {
    // cleanup resize event listeners
    window.removeEventListener('resize', this._throttledResize)
    if (this._mediaquery) {
      this._mediaquery.removeListener(this.onResize)
      delete this._mediaquery
    }

    clearInterval(this._updateId)
  }

  update = () => {
    const { loadLiveLog, liveLog, user, trip, loadComments } = this.props

    let params = {
      lastUpdatedAt: liveLog.updatedAt
    }
    if (liveLog.assetId) {
      params.routeId = liveLog.assetId
    }

    loadLiveLog(user.id, params).catch((e) => {
      if (e.details[0].error_code !== 'user_has_no_live_log') {
        throw e
      }
    })
    const objectType = 'trip'
    const objectId = trip.id
    loadComments(objectType, objectId)
  }

  onResize = () => {
    const { collapse } = this.state
    const iw = window.innerWidth
    const shouldCollapse = iw < CONTENT_COLLAPSE_WIDTH

    if (collapse !== shouldCollapse) {
      this.setState({ collapse: shouldCollapse })
    }
  }

  // we need to fake a resize event here so that MapPanel and SampleGraph
  // will expand to fill the space the SidePanel is leaving.
  // this IE-inclusive event trigger solution taken from https://stackoverflow.com/a/31899998
  toggleFullscreen = () => {
    var resizeEvent = window.document.createEvent('UIEvents')
    resizeEvent.initUIEvent('resize', true, false, window, 0)
    this.setState({ isFullscreen: !this.state.isFullscreen }, () => window.dispatchEvent(resizeEvent))
  }

  fullscreenState = () => {
    const { isFullscreen, collapse } = this.state
    return {
      isFullscreen: isFullscreen,
      mobileCollapse: collapse,
      toggleFullscreen: this.toggleFullscreen
    }
  }

  disallowStatusbar = () => {
    this.setState({ allowStatusBar: false })
  }

  render () {
    const {
      user,
      trip,
      troute,
      currentUser,
      pointsOfInterest,
      photos,
      liveLog,
      readOnly,
      location,
      intl
    } = this.props
    const {
      collapse,
      isFullscreen,
      allowStatusBar,
      sbHeight
    } = this.state
    const { privacy_code: privacyCode } = (location && location.query) || {}
    // TODO: get privacy code from trip record instead once
    // https://github.com/ridewithgps/ridewithgps/pull/8130 is deployed

    // Anonymous users still get a preference Map()
    const metricUnits = currentUser.preferences.get('metric_units', liveLog.metricUnits)

    let llState
    if (liveLog.finalized && moment().diff(moment(liveLog.updatedAt), 'h') <= 24) {
      llState = LL_COMPLETED
    } else if (liveLog.finalized && moment().diff(moment(liveLog.updatedAt), 'h') > 24) {
      llState = LL_UNSTARTED
    } else if (!liveLog.finalized) {
      llState = LL_IN_PROGRESS
    }

    return (
      <MetricUnitsContext.Provider value={metricUnits}>
        <div className={cx(base, styles.layout, collapse && !isFullscreen && styles.collapse)}>
          <GlobalNav />
          <FullscreenContext.Provider value={this.fullscreenState()}>
            <FullscreenButton intl={intl} padDown={liveLog.finalized && allowStatusBar} heightRef={sbHeight} />
          </FullscreenContext.Provider>
          {allowStatusBar && <LiveLogStatusBar
            monitorResize
            updateSBHeight={(val) => this.setState({ sbHeight: val })}
            user={user}
            liveLog={liveLog}
            completed={llState === LL_COMPLETED}
            unstarted={llState === LL_UNSTARTED}
            onRequestClose={this.disallowStatusbar} />}
          <div className={cx(styles.LiveLogPage, collapse && styles.collapse)}>
            <SidePanel
              className={cx(collapse && styles.sidePanel)}
              user={user}
              liveLog={liveLog}
              trip={trip}
              privacyCode={privacyCode}
              troute={troute}
              currentUser={currentUser}
              mobile={collapse}
              completed={llState === LL_COMPLETED}
              unstarted={llState === LL_UNSTARTED}
              readOnly={readOnly}
              fullscreen={isFullscreen} />
            <MapPanel
              className={cx(collapse && styles.mapPanel)}
              liveLog={liveLog}
              user={user}
              trip={trip}
              troute={troute}
              pointsOfInterest={pointsOfInterest}
              photos={photos}
              mobile={collapse}
              unstarted={llState === LL_UNSTARTED}
              fullscreen={isFullscreen} />
          </div>
        </div>
      </MetricUnitsContext.Provider>
    )
  }
}

const loadInitialData = ({ loadLiveLog, loadUser, params, location }) => {
  const userId = parseInt(params.userId, 10)

  return Promise.all([loadLiveLog(userId), loadUser(userId)])
}

const LiveLogPage = (props) => {
  const componentProps = { ...props }
  if (!componentProps.liveLog) {
    componentProps.liveLog = LiveLog({
      finalized: true,
      updatedAt: moment().subtract(25, 'hours') // HACK to get the right LiveLogStatusBar
    })
  }
  return <Loadable
    {...componentProps}
    component={_LiveLogPage}
    loadingText='Fetching radness'
    resourceLoaded={() => loadInitialData(props)} />
}

const mapStateToProps = (state, ownProps) => {
  const userId = parseInt(ownProps.params.userId, 10)
  const liveLog = liveLogSelectors.byUserId(state, userId)
  const user = userSelectors.find(state, userId)
  const trip = liveLog && tripSelectors.find(state, liveLog.tripId)
  let troute
  if (liveLog && liveLog.assetType === 'Route') {
    troute = routeSelectors.find(state, liveLog.assetId)
  } else if (liveLog && liveLog.assetType === 'Trip') {
    troute = tripSelectors.find(state, liveLog.assetId)
  }

  const pointsOfInterest = combinePoiIds(trip, troute).map((id) => pointOfInterestSelectors.find(state, id))
  const photos = trip && trip.photoIds.map((id) => photoSelectors.find(state, id))

  return {
    liveLog,
    user,
    trip,
    troute,
    pointsOfInterest,
    photos,
    currentUser: state.currentUser
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const { privacy_code: privacyCode } = ownProps.location.query

  return {
    loadLiveLog: (userId, params) => dispatch(userActions.liveLog(userId, privacyCode ? Object.assign({ privacyCode, ...params }) : { ...params }))
      .catch(e => {
        // it's OK if they have no live log, we have UI to display an informative message and reload the data in anticipation of a LL starting
        if (e.details[0].error_code !== 'user_has_no_live_log') {
          throw e
        }
      }),
    loadUser: (userId) => dispatch(userActions.privateUserShow(userId)),
    loadComments: (objectType, objectId) => dispatch(commentsActions.loadComments(objectType, objectId, privacyCode))
  }
}

export const LiveLogsContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(LiveLogPage))

const combinePoiIds = _.memoize((trip, troute) => {
  const routePois = (troute && troute.poiIds) || []
  const tripPois = (trip && trip.poiIds) || []

  return tripPois.concat(routePois)
})
